import * as Twilio from "@twilio/voice-sdk"
import { useCallback, useRef, useState } from "react"
import { useChatToken } from "services/communications"

type TwilioState = {
	deviceStatus: Twilio.Device.State
	callStatus: Twilio.Call.State
	callSid?: string
	muted: boolean
}

const twilioInitialState: TwilioState = {
	deviceStatus: Twilio.Device.State.Unregistered,
	callStatus: Twilio.Call.State.Closed,
	callSid: undefined,
	muted: false,
}

const getCallSid = (twilioCall: Twilio.Call) =>
	twilioCall.parameters?.CallSid ||
	twilioCall.parameters?.callSid ||
	twilioCall.customParameters?.get("CallSid") ||
	twilioCall.customParameters?.get("callSid")

export function useTwilio({
	To,
	onHangUp,
}: {
	To?: string
	onHangUp: VoidFunction
}) {
	const device = useRef<Twilio.Device | null>(null)
	const call = useRef<Twilio.Call | null>(null)
	const { refetch } = useChatToken(false)
	const [state, setState] = useState<TwilioState>(twilioInitialState)
	const hangup = useCallback(async () => {
		call.current = null
		setState((previousState) => ({
			...previousState,
			callStatus: Twilio.Call.State.Closed,
			callSid: undefined,
		}))
		device.current?.disconnectAll()
		await device.current?.unregister()
	}, [])
	const makeCall = useCallback(async () => {
		if (!To) {
			console.warn(
				`Unable to initiate call. The patient phone number seems empty: "${To}"`,
			)
			return null
		}

		if (!device.current) {
			console.warn(
				`Unable to initiate call. Twilio Device is not properly instantiated.`,
			)
			return null
		}

		console.debug(`Attempting to call ${To} ...`)

		// Clear any existing call first
		if (call.current) {
			call.current.disconnect()
			call.current = null
			setState((previousState) => ({
				...previousState,
				callStatus: Twilio.Call.State.Closed,
				callSid: undefined,
			}))
		}

		const twilioCall = await device.current.connect({ params: { To } })

		if (!twilioCall) {
			setState((previousState) => ({
				...previousState,
				callStatus: Twilio.Call.State.Closed,
				callSid: undefined,
			}))
			return null
		}

		call.current = twilioCall

		const sid = getCallSid(twilioCall)

		if (sid) {
			setState((previousState) => ({
				...previousState,
				callSid: sid,
			}))
		}

		// add listeners to the Call
		// "accepted" means the call has finished connecting and the state is now "open"

		call.current.on("accept", () => {
			console.debug("Call.accept")
			setState((previousState) => ({
				...previousState,
				callStatus: Twilio.Call.State.Open,
				callSid: getCallSid(twilioCall) ?? previousState.callSid,
			}))
		})
		call.current.on("disconnect", async () => {
			console.debug("Call.disconnect")
			call.current = null
			setState((previousState) => ({
				...previousState,
				callStatus: Twilio.Call.State.Closed,
				callSid: undefined,
			}))
			await device.current?.unregister()
		})
		call.current.on("cancel", async () => {
			console.debug("Call.cancel")
			call.current = null
			setState((previousState) => ({
				...previousState,
				callStatus: Twilio.Call.State.Closed,
				callSid: undefined,
			}))
			await device.current?.unregister()
		})
		call.current.on("error", async () => {
			console.debug("Call.error")
			call.current = null
			setState((previousState) => ({
				...previousState,
				callStatus: Twilio.Call.State.Closed,
				callSid: undefined,
			}))
			await device.current?.unregister()
		})
		call.current.on("mute", (isMuted: boolean) => {
			console.debug("Call.mute", { isMuted })
			setState((previousState) => ({
				...previousState,
				muted: isMuted,
			}))
		})
		call.current.on("reconnected", () => {
			console.debug("Call.reconnected")
			setState((previousState) => ({
				...previousState,
				callStatus: Twilio.Call.State.Open,
				callSid: getCallSid(twilioCall) ?? previousState.callSid,
			}))
		})
		call.current.on("reconnecting", () => {
			console.debug("Call.reconnecting")
			setState((previousState) => ({
				...previousState,
				callStatus: Twilio.Call.State.Reconnecting,
			}))
		})
		call.current.on("reject", async (error) => {
			console.debug("Call.reject", error)
			call.current = null
			setState((previousState) => ({
				...previousState,
				callStatus: Twilio.Call.State.Closed,
				callSid: undefined,
			}))
			await device.current?.unregister()
		})
		call.current.on("ringing", () => {
			console.debug("Call.ringing")
			setState((previousState) => ({
				...previousState,
				callStatus: Twilio.Call.State.Ringing,
				callSid: getCallSid(twilioCall) ?? previousState.callSid,
			}))
		})

		return twilioCall
	}, [To])
	const intitializeDevice = useCallback(async () => {
		const { data: initialTokenRequest } = await refetch({
			throwOnError: true,
		})

		if (!initialTokenRequest?.token || device.current) {
			return
		}

		device.current = new Twilio.Device(initialTokenRequest.token, {
			logLevel: !!import.meta.env.PROD ? "error" : "info",
		})

		device.current.on(Twilio.Device.EventName.Destroyed, () => {
			console.debug(`Twilio.Device.EventName.Destroyed`)
			setState((previousState) => ({
				...previousState,
				deviceStatus: Twilio.Device.State.Destroyed,
			}))
		})
		device.current.on(Twilio.Device.EventName.Error, () => {
			console.warn(`Twilio.Device.EventName.Error`)
		})
		device.current.on(Twilio.Device.EventName.Registered, async () => {
			console.debug(`Twilio.Device.EventName.Registered`)
			setState((previousState) => ({
				...previousState,
				deviceStatus: Twilio.Device.State.Registered,
			}))

			const call = await makeCall()

			if (!call) {
				// just unwind current device if call fails to initiate
				await hangup()
			}
		})
		device.current.on(Twilio.Device.EventName.Registering, () => {
			console.debug(`Twilio.Device.EventName.Registering`)
			setState((previousState) => ({
				...previousState,
				deviceStatus: Twilio.Device.State.Registering,
			}))
		})
		device.current.on(Twilio.Device.EventName.TokenWillExpire, async () => {
			console.debug(`Twilio.Device.EventName.TokenWillExpire`)

			console.debug(`Refetching Twilio token`)
			const { data: newTokenRequest } = await refetch({
				throwOnError: true,
			})

			if (!!newTokenRequest?.token && device.current) {
				console.debug(`Updating Twilio device with new token`)
				device.current.updateToken(newTokenRequest?.token)
			} else {
				console.warn(`Token refresh failed or no token available`)
			}
		})
		device.current.on(Twilio.Device.EventName.Unregistered, () => {
			console.debug(`Twilio.Device.EventName.Unregistered`)
			call.current?.disconnect()
			call.current = null
			device.current?.audio?.unsetInputDevice()
			device.current = null
			setState((previousState) => ({
				...previousState,
				deviceStatus: Twilio.Device.State.Unregistered,
				callStatus: Twilio.Call.State.Closed,
				callSid: undefined,
			}))
			onHangUp()
		})

		device.current.register()
	}, [hangup, makeCall, onHangUp, refetch])

	return {
		callSid:
			state.callSid ?? (call.current ? getCallSid(call.current) : undefined),
		deviceAvailable: state.deviceStatus === Twilio.Device.State.Registered,
		callOpen:
			state.callStatus === Twilio.Call.State.Open ||
			state.callStatus === Twilio.Call.State.Ringing,
		muted: state.muted,
		intitializeDevice,
		hangup,
		mute: () => {
			call.current?.mute(!state.muted)
		},
		sendDigit: (digit: string) => {
			call.current?.sendDigits(digit)
		},
		setInput: async (deviceId: string) => {
			await device.current?.audio?.setInputDevice(deviceId)
		},
		setOutput: async (deviceId: string) => {
			await device.current?.audio?.speakerDevices.set(deviceId)
		},
	}
}
